import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { useLanguage } from '../i18n/LanguageContext';
import NavBar from '../components/NavBar';

const Documents = ({ user, onLogout }) => {
  const location = useLocation();
  const { t } = useLanguage();
  const [documents, setDocuments] = useState([]);
  const [checklist, setChecklist] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [documentType, setDocumentType] = useState('other');
  const [description, setDescription] = useState('');
  const [expirationDate, setExpirationDate] = useState('');

  const fetchDocuments = async () => {
    try {
      const [docsRes, checklistRes] = await Promise.all([
        axios.get(`${API}/legal-documents`),
        axios.get(`${API}/legal-documents/checklist`)
      ]);
      setDocuments(docsRes.data);
      setChecklist(checklistRes.data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    const params = new URLSearchParams();
    params.append('document_type', documentType);
    if (description) params.append('description', description);
    if (expirationDate) params.append('expiration_date', expirationDate);

    try {
      await axios.post(`${API}/legal-documents?${params.toString()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded successfully');
      setDescription('');
      setDocumentType('other');
      setExpirationDate('');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/legal-documents/${doc.id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const handleDelete = async (docId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      try {
        await axios.delete(`${API}/legal-documents/${docId}`);
        toast.success('Document deleted');
        fetchDocuments();
      } catch (error) {
        toast.error('Failed to delete document');
      }
    }
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getDocumentLabel = (type) => {
    return t(`documents.${type}`) || type;
  };

  const getExpirationStatus = (expDate) => {
    if (!expDate) return null;
    const now = new Date();
    const exp = new Date(expDate);
    const daysUntil = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
    if (daysUntil < 0) return 'expired';
    if (daysUntil <= 30) return 'soon';
    return 'valid';
  };

  const checklistItems = [
    { type: 'will', question: t('documents.checklist.willQ'), icon: 'fa-scroll' },
    { type: 'healthcare_directive', question: t('documents.checklist.healthcareQ'), icon: 'fa-file-medical' },
    { type: 'living_trust', question: t('documents.checklist.trustQ'), icon: 'fa-landmark' },
    { type: 'power_of_attorney', question: t('documents.checklist.poaQ'), icon: 'fa-gavel' }
  ];

  return (
    <div className="dashboard-container" data-testid="documents-page">
      <NavBar onLogout={onLogout} />

      <div className="dashboard-content page-container">
        <h1 className="page-title" data-testid="documents-title">{t('documents.title')}</h1>

        {/* Document Checklist */}
        <div className="section-card" style={{ marginBottom: '2rem' }} data-testid="document-checklist">
          <h2 className="section-title">{t('documents.checklist.title')}</h2>
          <p style={{ color: '#6b8899', marginBottom: '1.25rem' }}>{t('documents.checklist.subtitle')}</p>
          
          <div className="doc-checklist">
            {checklistItems.map((item) => {
              const status = checklist[item.type];
              const isUploaded = status?.uploaded;
              const expStatus = isUploaded ? getExpirationStatus(status?.expiration_date) : null;
              const isExpired = expStatus === 'expired';

              let itemClass = 'checklist-item ';
              let iconClass = 'checklist-icon ';
              if (isExpired) {
                itemClass += 'checklist-item-expired';
                iconClass += 'checklist-icon-expired';
              } else if (isUploaded) {
                itemClass += 'checklist-item-uploaded';
                iconClass += 'checklist-icon-uploaded';
              } else {
                itemClass += 'checklist-item-missing';
                iconClass += 'checklist-icon-missing';
              }

              return (
                <div key={item.type} className={itemClass} data-testid={`checklist-${item.type}`}>
                  <div className={iconClass}>
                    <i className={`fa-solid ${item.icon}`}></i>
                  </div>
                  <div className="checklist-details">
                    <div className="checklist-question">{item.question}</div>
                    <div className="checklist-status">
                      {isUploaded ? (
                        <>
                          <span style={{ color: isExpired ? '#dc2626' : '#16a34a' }}>
                            {isExpired ? (
                              <><i className="fa-solid fa-triangle-exclamation"></i> {t('documents.expired')}</>
                            ) : (
                              <><i className="fa-solid fa-circle-check"></i> {t('documents.checklist.uploaded')}</>
                            )}
                          </span>
                          {status.expiration_date && (
                            <span className={`expiration-badge ${
                              expStatus === 'expired' ? 'expiration-expired' : 
                              expStatus === 'soon' ? 'expiration-soon' : 'expiration-valid'
                            }`} style={{ marginLeft: '0.5rem' }}>
                              {t('documents.expires')} {formatDate(status.expiration_date)}
                            </span>
                          )}
                        </>
                      ) : (
                        <span style={{ color: '#d97706' }}>
                          <i className="fa-solid fa-circle-xmark"></i> {t('documents.checklist.missing')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Upload Section */}
        <div className="section-card" style={{ marginBottom: '2rem' }} data-testid="upload-section">
          <h2 className="section-title">{t('documents.uploadNew')}</h2>
          <p style={{ color: '#6b8899', marginBottom: '1.5rem' }}>{t('documents.uploadDesc')}</p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group">
              <label htmlFor="document_type">{t('documents.docType')}</label>
              <select
                id="document_type"
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                data-testid="document-type-select"
              >
                <option value="will">{t('documents.will')}</option>
                <option value="power_of_attorney">{t('documents.power_of_attorney')}</option>
                <option value="healthcare_directive">{t('documents.healthcare_directive')}</option>
                <option value="living_trust">{t('documents.living_trust')}</option>
                <option value="insurance">{t('documents.insurance')}</option>
                <option value="other">{t('documents.other')}</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="description">{t('documents.description')}</label>
              <input
                id="description"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('documents.descPlaceholder')}
                data-testid="document-description-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="expiration_date">{t('documents.expirationDate')}</label>
              <input
                id="expiration_date"
                type="date"
                value={expirationDate}
                onChange={(e) => setExpirationDate(e.target.value)}
                data-testid="document-expiration-input"
              />
            </div>
          </div>

          <div style={{ 
            border: '2px dashed #2c5364', 
            borderRadius: '12px', 
            padding: '2rem', 
            textAlign: 'center',
            background: '#f8f9fa'
          }}>
            <input
              type="file"
              id="document-upload"
              style={{ display: 'none' }}
              onChange={handleUpload}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
              disabled={uploading}
              data-testid="document-upload-input"
            />
            <label htmlFor="document-upload" style={{ cursor: uploading ? 'not-allowed' : 'pointer', display: 'block' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.75rem', color: '#2c5364' }}>
                <i className="fa-solid fa-cloud-arrow-up"></i>
              </div>
              <p style={{ color: '#2c5364', fontWeight: '600', marginBottom: '0.5rem' }}>
                {uploading ? t('documents.uploading') : t('documents.clickUpload')}
              </p>
              <p style={{ color: '#6b8899', fontSize: '0.9rem' }}>
                {t('documents.supportedFormats')}
              </p>
            </label>
          </div>
        </div>

        {/* Documents List */}
        <div className="section-card">
          <h2 className="section-title">{t('documents.storedDocs')}</h2>
          
          {loading ? (
            <p>{t('common.loading')}</p>
          ) : documents.length === 0 ? (
            <div className="empty-state" data-testid="empty-documents">
              <div style={{ fontSize: '3rem', marginBottom: '1rem', color: '#94a3b8' }}>
                <i className="fa-solid fa-folder-open"></i>
              </div>
              <h3>{t('documents.noDocs')}</h3>
              <p style={{ color: '#6b8899' }}>{t('documents.noDocsDesc')}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {documents.map((doc) => {
                const expStatus = getExpirationStatus(doc.expiration_date);
                return (
                  <div 
                    key={doc.id} 
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '1rem 1.5rem', 
                      background: expStatus === 'expired' ? '#fef2f2' : '#f8f9fa', 
                      borderRadius: '8px',
                      borderLeft: `4px solid ${expStatus === 'expired' ? '#dc2626' : '#2c5364'}`,
                      flexWrap: 'wrap',
                      gap: '0.75rem'
                    }}
                    data-testid={`document-item-${doc.id}`}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: 1, minWidth: '200px' }}>
                      <div style={{ 
                        width: '40px', height: '40px', borderRadius: '8px', 
                        background: '#e7f3ff', display: 'flex', alignItems: 'center', 
                        justifyContent: 'center', color: '#2c5364', flexShrink: 0
                      }}>
                        <i className="fa-solid fa-file-lines"></i>
                      </div>
                      <div>
                        <h4 style={{ margin: 0, color: '#2c5364' }}>{doc.file_name}</h4>
                        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '0.25rem', alignItems: 'center' }}>
                          <span style={{ 
                            background: '#e7f3ff', padding: '0.15rem 0.5rem', 
                            borderRadius: '4px', fontSize: '0.8rem', color: '#0c5460'
                          }}>
                            {getDocumentLabel(doc.document_type)}
                          </span>
                          <span style={{ color: '#6b8899', fontSize: '0.85rem' }}>
                            {formatFileSize(doc.file_size)}
                          </span>
                          <span style={{ color: '#6b8899', fontSize: '0.85rem' }}>
                            {t('documents.uploaded')} {formatDate(doc.uploaded_at)}
                          </span>
                          {doc.expiration_date && (
                            <span className={`expiration-badge ${
                              expStatus === 'expired' ? 'expiration-expired' : 
                              expStatus === 'soon' ? 'expiration-soon' : 'expiration-valid'
                            }`}>
                              {expStatus === 'expired' ? t('documents.expired') : t('documents.expires')} {formatDate(doc.expiration_date)}
                            </span>
                          )}
                        </div>
                        {doc.description && (
                          <p style={{ margin: '0.5rem 0 0', color: '#4a6572', fontSize: '0.9rem' }}>
                            {doc.description}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        className="submit-button"
                        style={{ width: 'auto', padding: '0.5rem 1rem', fontSize: '0.9rem' }}
                        onClick={() => handleDownload(doc)}
                        data-testid={`download-doc-${doc.id}`}
                      >
                        <i className="fa-solid fa-download"></i> {t('common.download')}
                      </button>
                      <button
                        className="delete-button"
                        onClick={() => handleDelete(doc.id)}
                        data-testid={`delete-doc-${doc.id}`}
                      >
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Privacy Notice */}
        <div style={{ 
          marginTop: '2rem', padding: '1rem 1.5rem', 
          background: '#fff3cd', borderRadius: '8px',
          borderLeft: '4px solid #ffc107'
        }}>
          <p style={{ margin: 0, color: '#856404', fontSize: '0.9rem' }}>
            <strong><i className="fa-solid fa-lock"></i> {t('documents.privacyNotice')}</strong> {t('documents.privacyDesc')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Documents;
